import { editableItems } from './edits';

import type { ReviewScene } from './edits';
import type { RewriteTarget } from '@platform/schema/generation';

/**
 * What the console says between asking for a regeneration and seeing one — spec 013 §12.1.
 *
 * A regeneration is a queued job and a planner turn is a minute, so there are four states
 * and the screen used to show one of them. It said "Queued" on success and nothing at all
 * while the request was in flight, nothing when it failed, and nothing when the job
 * finished — so a reviewer clicked, saw no change, and had no way to tell a slow job from
 * a lost one.
 */

/** Job statuses that mean the queue is finished with it, whatever the outcome. */
const SETTLED = new Set(['SUCCEEDED', 'PARTIAL', 'FAILED']);

export function jobSettled(status: string | undefined): boolean {
  return status !== undefined && SETTLED.has(status);
}

/** One line about a queued job, in the words of whatever it was asked to do. */
export interface JobNote {
  severity: 'info' | 'success' | 'error';
  message: string;
}

/**
 * What a job was asked to do, which is all that differs between two of them — GEN-76.
 *
 * Applying a finding is queued and followed exactly as a rewrite is, so it used to be
 * reported in the rewrite's words: a reviewer who clicked "ask the editor to fix this"
 * was told "the new draft is below", about a draft they had not asked for. The states are
 * the same four; the vocabulary is not.
 */
export type JobSubject = 'rewrite' | 'edit';

/** How long the job has run, or `undefined` until its first poll answers. */
type Elapsed = string | undefined;

interface JobWords {
  sending: string;
  unqueued: string;
  running: (elapsed: Elapsed) => string;
  failed: (elapsed: Elapsed) => string;
  done: string;
}

/** ` for 4m 5s`, or nothing before the first poll has answered. */
const spent = (preposition: string, elapsed: Elapsed): string =>
  elapsed === undefined ? '' : ` ${preposition} ${elapsed}`;

// GEN-195: a fixed "about a minute" read as stuck for the twenty a timed-out row and its
// retry can take, so the running lines count instead of promising.
const WORDS: Record<JobSubject, JobWords> = {
  rewrite: {
    sending: 'Sending…',
    unqueued: 'The regeneration could not be queued. Nothing has changed.',
    running: (elapsed) => `Writing a new draft${spent('for', elapsed)}.`,
    failed: (elapsed) =>
      `The planner gave up${spent('after', elapsed)}. The draft is unchanged.`,
    done: 'The new draft is below.',
  },
  edit: {
    sending: 'Asking the editor…',
    unqueued:
      'The editor could not be asked. Nothing has changed and the finding is still open. Next step: try Fix with AI again, or edit the scene by hand.',
    running: (elapsed) =>
      `The editor has been working${spent('for', elapsed)}. Nothing changes until you apply it.`,
    failed: (elapsed) =>
      `The editor gave up${spent('after', elapsed)}. The scene is unchanged and the finding is still open. Next step: try Fix with AI again, or edit the scene by hand.`,
    // Deliberately not "here is the change": the editor is allowed to come back having
    // changed nothing, and what it did is on the finding below this line.
    done: 'The editor has finished.',
  },
};

/**
 * One line for whichever of the four states the reviewer is in.
 *
 * `sending` outranks a previous job's outcome: a second click while the first result is
 * still on screen must not read as already finished.
 */
export function jobNote(
  state: {
    sending: boolean;
    failed: boolean;
    status: string | undefined;
    elapsed?: Elapsed;
  },
  subject: JobSubject,
): JobNote | undefined {
  const words = WORDS[subject];

  if (state.sending) return { severity: 'info', message: words.sending };

  if (state.failed) return { severity: 'error', message: words.unqueued };

  if (state.status === undefined) return undefined;

  if (!jobSettled(state.status))
    return { severity: 'info', message: words.running(state.elapsed) };

  if (state.status === 'FAILED')
    return { severity: 'error', message: words.failed(state.elapsed) };

  return { severity: 'success', message: words.done };
}

/**
 * What one regeneration asks for, before the lesson it belongs to is known — GEN-47.
 *
 * Derived from `rewriteTargetSchema` rather than restated, so a sixth grain is a compile
 * error here instead of a 400 at runtime. The lesson id is dropped and added back by
 * `ReviewWorkspace`, the only component that has it: a scene editor knows its scene and
 * not which lesson is being reviewed, and a grain carrying a guess would fork the wrong
 * placement.
 */
export type RewriteRequest = DistributiveOmit<RewriteTarget, 'lessonId'>;

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

/** One entry in the picker: what it is called, and what pressing it asks for. */
export interface RewriteChoice {
  key: string;
  label: string;
  request: RewriteRequest;
}

/** An option's own words, short enough to pick from a list by. */
const shortened = (label: string): string =>
  label.length <= 40 ? label : `${label.slice(0, 39)}…`;

/**
 * Everything in one scene that can be written again — GEN-47.
 *
 * Derived from the scene rather than listed per kind: a narrative scene has no question
 * and no options, so it offers one choice and the picker does not appear at all. The
 * eleventh mechanic gets its options here for free, which a switch would not give it.
 */
export function rewriteChoices(scene: ReviewScene): RewriteChoice[] {
  const sceneId = scene.id;
  const questionId = 'questionId' in scene ? scene.questionId : undefined;

  return [
    {
      key: 'SCENE',
      label: 'The whole scene',
      request: { targetType: 'SCENE', sceneId },
    },
    ...(questionId === undefined
      ? []
      : [
          {
            key: 'QUESTION',
            label: 'The question',
            request: {
              targetType: 'QUESTION' as const,
              sceneId,
              questionId,
            },
          },
          {
            key: 'EXPLANATION',
            label: 'The explanation',
            request: {
              targetType: 'EXPLANATION' as const,
              sceneId,
              questionId,
            },
          },
        ]),
    ...editableItems(scene).map((item, index) => ({
      key: `OPTION:${item.id}`,
      label: `Option ${String(index + 1)} — ${shortened(item.label)}`,
      request: { targetType: 'OPTION' as const, sceneId, itemId: item.id },
    })),
  ];
}

/**
 * What a whole-lesson regeneration will leave alone — GEN-47.
 *
 * Said before the button is pressed, for the same reason `sharedWarning` is: a reviewer
 * who corrected three scenes by hand and asks for the lesson again needs to know those
 * three are spared *now*, not to discover it by diffing afterwards.
 */
export function handEditedNote(
  handEdited: number,
  scenes: number,
): { message?: string; blocked: boolean } {
  if (handEdited === 0) return { blocked: false };

  if (handEdited >= scenes)
    return {
      blocked: true,
      message:
        'Every scene in this lesson has been hand-edited, so a lesson regeneration would write nothing. Regenerate a scene to overwrite one.',
    };

  return {
    blocked: false,
    message: `${String(handEdited)} of ${String(scenes)} scenes have been hand-edited. They are left exactly as they are; the other ${String(scenes - handEdited)} are written again.`,
  };
}
